/**
 * CLIENT-SIDE IMAGE DOWNSCALING — single source of truth.
 *
 * WHY THIS EXISTS
 * Homeowners upload straight from a phone camera. Three photos already in the
 * production bucket are 3.4 MB / 5400x3600, 2.5 MB / 3024x4032 and 845 KB /
 * 1080x2340. Those are fine on a laptop but on a phone the browser has to pull
 * megabytes over cellular AND decode a 19-megapixel bitmap on the main thread.
 * During that decode the <img> has no pixels yet — which is exactly the window
 * in which a "blank image container" is visible to a real user.
 *
 * Shrinking at the source is the only fix that helps every surface at once
 * (job card, job detail, dashboard, admin moderation) and every future viewer,
 * because the smaller file is what lands in the bucket.
 *
 * RULES IMPLEMENTED
 *   - Any image wider (or taller) than MAX_EDGE px is scaled down to fit.
 *   - Aspect ratio is always preserved (single uniform scale factor).
 *   - Re-encoded to WebP, quality stepped down until it fits TARGET_BYTES.
 *   - If WebP is not supported by the browser, falls back to JPEG.
 *   - If anything at all goes wrong, the ORIGINAL file is returned unchanged.
 *     A failed optimisation must never cost the homeowner their photo.
 *   - GIFs are passed through untouched (canvas would kill the animation).
 */

export const MAX_EDGE = 2000;
/** ~500 KB ceiling per photo. */
export const TARGET_BYTES = 500 * 1024;

const QUALITY_STEPS = [0.82, 0.72, 0.62, 0.52, 0.42];

export interface CompressResult {
  file: File;
  /** True when we actually produced a new, smaller file. */
  changed: boolean;
  originalBytes: number;
  bytes: number;
  width: number;
  height: number;
  format: string;
}

/** Feature detection for canvas WebP encoding (Safari < 14 cannot). */
const canEncodeWebp = (): boolean => {
  try {
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 1;
    return c.toDataURL('image/webp').startsWith('data:image/webp');
  } catch (_e) {
    return false;
  }
};

const decode = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('decode-failed'));
    };
    img.src = url;
  });

const toBlob = (canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> =>
  new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b), type, quality);
    } catch (_e) {
      resolve(null);
    }
  });

const renamed = (name: string, ext: string) => {
  const base = name.replace(/\.[^.]+$/, '') || 'photo';
  // Keep the key clean: storage keys are built from this name downstream.
  return `${base.slice(0, 60).replace(/[^\w\-. ]+/g, '')}.${ext}`;
};

/**
 * Downscale + re-encode one image in the browser.
 * ALWAYS resolves — never throws — so the caller can upload unconditionally.
 */
export const compressImage = async (
  file: File,
  opts: { maxEdge?: number; targetBytes?: number } = {}
): Promise<CompressResult> => {
  const maxEdge = opts.maxEdge ?? MAX_EDGE;
  const targetBytes = opts.targetBytes ?? TARGET_BYTES;
  const passthrough = (w = 0, h = 0): CompressResult => ({
    file,
    changed: false,
    originalBytes: file.size,
    bytes: file.size,
    width: w,
    height: h,
    format: file.type || 'image/jpeg',
  });

  if (typeof document === 'undefined') return passthrough();
  if (!file.type.startsWith('image/')) return passthrough();
  // Animated formats and vectors must not go through a canvas.
  if (file.type === 'image/gif' || file.type === 'image/svg+xml') return passthrough();

  let img: HTMLImageElement;
  try {
    img = await decode(file);
  } catch (_e) {
    // HEIC on a browser that cannot decode it, corrupt file, etc.
    return passthrough();
  }

  const w0 = img.naturalWidth || img.width;
  const h0 = img.naturalHeight || img.height;
  if (!w0 || !h0) return passthrough();

  const scale = Math.min(1, maxEdge / Math.max(w0, h0));
  const needsResize = scale < 1;
  const needsShrink = file.size > targetBytes;

  // Already small in both dimensions AND bytes -> leave it completely alone.
  if (!needsResize && !needsShrink) return passthrough(w0, h0);

  const width = Math.max(1, Math.round(w0 * scale));
  const height = Math.max(1, Math.round(h0 * scale));

  let canvas: HTMLCanvasElement;
  try {
    canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return passthrough(w0, h0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);
  } catch (_e) {
    // Canvas can be tainted or the bitmap too large for the device.
    return passthrough(w0, h0);
  }

  const mime = canEncodeWebp() ? 'image/webp' : 'image/jpeg';
  const ext = mime === 'image/webp' ? 'webp' : 'jpg';

  let best: Blob | null = null;
  for (const q of QUALITY_STEPS) {
    const blob = await toBlob(canvas, mime, q);
    if (!blob) break;
    best = blob;
    if (blob.size <= targetBytes) break;
  }

  if (!best) return passthrough(w0, h0);
  // Never upload something LARGER than what the homeowner picked.
  if (best.size >= file.size && !needsResize) return passthrough(w0, h0);

  try {
    const out = new File([best], renamed(file.name, ext), { type: mime, lastModified: Date.now() });
    return {
      file: out,
      changed: true,
      originalBytes: file.size,
      bytes: out.size,
      width,
      height,
      format: mime,
    };
  } catch (_e) {
    return passthrough(w0, h0);
  }
};

export const formatBytes = (n: number): string => {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};
